"use client";

import * as X from "@/ui";

import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";

import "swiper/css";
import { RatingCard, TestimonialSlide as TestimonialItem } from "@/providers/strapi/content/types/components/traveler.component";
import ReactMarkdown from "react-markdown";


// type Testimonial = {
//   photo: X.ImageSrc;
//   name: X.Localization<string>;
//   text: X.Localization<string>;
// };


export function TestimonialsSection(props: X.PageProps<{
  id?: string;
  // title: string;//X.Localization<React.ReactNode>;
  // subtitle?: X.Localization<React.ReactNode>;
  testimonials: TestimonialItem[];
  className?: string;
  cta: React.ReactNode | {
    mobile: React.ReactNode;
    desktop: React.ReactNode;
  };
  ratingCard: RatingCard;
}>) {
  const swiperMobileRef = X.useRef<SwiperInstance | null>(null);
  const swiperDesktopRef = X.useRef<SwiperInstance | null>(null);
  const onMobileSwipeCallbacksRef = X.useRef<Set<() => void>>(new Set());
  const onDesktopSwipeCallbacksRef = X.useRef<Set<() => void>>(new Set());

  const ctaMobile: React.ReactNode = (
    typeof props.cta === "object" && "desktop" in props.cta!
      ? props.cta.mobile
      : props.cta
  );

  const ctaDesktop: React.ReactNode = (
    typeof props.cta === "object" && "desktop" in props.cta!
      ? props.cta.desktop
      : props.cta
  );

  return (
    <section id={props.id} className={X.clsx(
      "xl:max-w-content xl:mx-auto",
      props.id && "scroll-mt-[120px]",
      props.className,
    )}>
      <div className="bg-black flex flex-col xl:flex-row text-white xl:rounded-4xl">
        <div className="xl:p-6 pr-0 flex flex-col">
          <Ratings
            locale={props.locale}
            title={props.ratingCard.title}
            subtitle={props.ratingCard.subTitle}
            ratingCard={props.ratingCard}
          />
        </div>

        <div className="relative xl:w-[60%] flex flex-col xl:justify-end xl:-right-[1px]">
          <div className={X.clsx(
            X.mobileOnly(),
            "bg-white absolute top-[60px] left-0 right-0 bottom-0",
          )} />

          <div className="text-black relative">
            <TestimonialsSlider
              device="mobile"
              locale={props.locale}
              testimonials={props.testimonials}
              swiperRef={swiperMobileRef}
              onSwipe={() => onMobileSwipeCallbacksRef.current.forEach(onSwipe => onSwipe())}
            />
            <TestimonialsSlider
              device="desktop"
              locale={props.locale}
              testimonials={props.testimonials}
              swiperRef={swiperDesktopRef}
              onSwipe={() => onDesktopSwipeCallbacksRef.current.forEach(onSwipe => onSwipe())}
            />
            <Switch
              device="mobile"
              locale={props.locale}
              swiperRef={swiperMobileRef}
              registerSwipeCallback={callback => {
                onMobileSwipeCallbacksRef.current.add(callback);
                return {
                  unregister: () => onMobileSwipeCallbacksRef.current.delete(callback),
                };
              }}
            />
            <Switch
              device="desktop"
              locale={props.locale}
              swiperRef={swiperDesktopRef}
              registerSwipeCallback={callback => {
                onDesktopSwipeCallbacksRef.current.add(callback);
                return {
                  unregister: () => onDesktopSwipeCallbacksRef.current.delete(callback),
                };
              }}
            />
          </div>
        </div>
      </div>

      <div className={X.clsx(
        X.desktopOnly("flex"),
        "mt-8 mb-0 w-[520px] flex-row justify-center",
      )}>
        {ctaDesktop}
      </div>

      <div className={X.clsx(
        X.mobileOnly(),
        "mt-12 flex flex-col items-center"
      )}>
        {ctaMobile}
      </div>
    </section>
  );
}


function Ratings(props: X.PageProps<{
  title?: string;//X.Localization<React.ReactNode>;
  subtitle?: string | null;//X.Localization<React.ReactNode>;
  ratingCard: RatingCard;
}>) {
  // const tr = X.useTranslator();

  return (
    <div className="px-mobile-margin py-8 xl:px-8 xl:pr-4 text-center flex flex-col justify-around flex-1">
      {/* <h2 className="text-[30px] xl:text-[36px] [&_strong]:font-medium mb-4 leading-tight">
        {tr(props.title)}

      </h2> */}
      {props.title && <ReactMarkdown components={{
        p: ({ ...props }) => <h2 {...props} className="text-[30px] xl:text-[36px] [&_strong]:font-medium mb-4 leading-tight" />,
        h1: ({ ...props }) => <h2 {...props} className="text-[30px] xl:text-[36px] [&_strong]:font-medium mb-4 leading-tight" />,
        h2: ({ ...props }) => <h2 {...props} className="text-[30px] xl:text-[36px] [&_strong]:font-medium mb-4 leading-tight" />,
        h3: ({ ...props }) => <h2 {...props} className="text-[30px] xl:text-[36px] [&_strong]:font-medium mb-4 leading-tight" />,
        h4: ({ ...props }) => <h2 {...props} className="text-[30px] xl:text-[36px] [&_strong]:font-medium mb-4 leading-tight" />
      }}>
        {props.title}
      </ReactMarkdown>}
      {props.subtitle && (
        <ReactMarkdown components={{
          p: ({ ...props }) => <p {...props} className="mb-5 font-light text-[17px] [&_strong]:font-medium xl:text-[24px]" />
        }}>
          {props.subtitle}
        </ReactMarkdown>
        // <p className="mb-5 font-light text-[17px] [&_strong]:font-medium xl:text-[24px]">
        //   {tr(props.subtitle)}
        //   {props.subtitle}
        // </p>
      )}

      <X.AppRatings locale={props.locale} ratingCard={props.ratingCard} />
    </div>
  );
}


function TestimonialsSlider(props: X.PageProps<{
  device: "mobile" | "desktop";
  testimonials: TestimonialItem[];
  swiperRef: React.RefObject<SwiperInstance | null>;
  onSwipe: () => void;
}>) {
  return (
    <div className={X.clsx(
      "xl:h-full",
      props.device === "mobile" && X.mobileOnly(),
      props.device === "desktop" && X.desktopOnly("block"),
    )}>
      <Swiper
        spaceBetween={props.device === "mobile" ? 16 : 32}
        slidesPerView={props.device === "mobile" ? 1.15 : 1.25}
        centeredSlides={props.device === "mobile" ? true : true}
        centeredSlidesBounds={false}

        className="xl:h-full"
        onSlideChange={props.onSwipe}
        onSwiper={swiper => {
          props.swiperRef.current = swiper;
          setTimeout(() => props.onSwipe(), 100);
        }}
      >
        {props.testimonials.map((testimonial, i) => (
          <SwiperSlide key={i}>
            <TestimonialSlide
              locale={props.locale}
              testimonial={testimonial}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}


function TestimonialSlide(props: X.PageProps<{
  testimonial: TestimonialItem;
}>) {
  // const tr = X.useTranslator();

  return (
    <div className={X.clsx("w-full h-full")}>
      <div className="flex flex-col w-full items-center h-full bg-white p-8 rounded-t-4xl xl:mt-12">
        <div className="mb-4 w-[80px] h-[80px] relative">
          <X.Image
            src={props.testimonial.avatar.url}
            alt={
              // tr({ fr: props.testimonial.name, en: props.testimonial.name })
              props.testimonial.name
            }
            className="object-cover rounded-xl"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            fill
          />
        </div>
        <h3 className="text-[14px] xl:text-[20px] font-bold mb-2 text-center xl:max-w-[280px]">
          {/* {tr({ fr: props.testimonial.name, en: props.testimonial.name })} */}
          <ReactMarkdown>
            {props.testimonial.name}
          </ReactMarkdown>
        </h3>
        {/* {props.testimonial.text[props.locale]?.map((text, i) => (
          <p key={i} className="text-[13px] xl:text-[18px] leading-tight text-center">{text}</p>
        ))} */}
        {/* <p className="text-[13px] xl:text-[18px] leading-tight text-center">{props.testimonial.text[props.locale]}</p> */}
        <ReactMarkdown components={{
          p: ({ ...props }) => <p {...props} className="text-[13px] xl:text-[18px] leading-tight text-center" />,
        }
        }>
          {props.testimonial.content}
        </ReactMarkdown>
      </div>
    </div>
  );
}


function Switch(props: X.PageProps<{
  device: "mobile" | "desktop";
  swiperRef: React.RefObject<SwiperInstance | null>;
  registerSwipeCallback: (callback: () => void) => { unregister: () => void };
}>) {
  const instance = X.useInstance();

  /* the only reliable way to see if clickable or not; `isBeginning` and `isEnd`
   * doe not work properly */
  const swiper = props.swiperRef.current;
  const activeIndex = swiper?.activeIndex ?? 0;
  const lastIndex = (swiper?.slides.length ?? 1) - 1;
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === lastIndex;

  X.useMountEffect(() => {
    const registration = props.registerSwipeCallback(instance.render);
    return registration.unregister;
  });

  return (
    <div className={X.clsx(
      props.device === "mobile" ? X.mobileOnly() : X.desktopOnly("flex"),
      "xl:absolute xl:-bottom-12 xl:left-0 xl:right-0 xl:-translate-x-[5px] flex justify-center gap-2 z-10",
    )}>
      {[
        {
          icon: <X.Icon.ChevronLeft />,
          onPress: () => props.swiperRef.current?.slidePrev(),
          active: !isFirst,
        },
        {
          icon: <X.Icon.ChevronRight />,
          onPress: () => props.swiperRef.current?.slideNext(),
          active: !isLast,
        },
      ].map((button, i) => (
        <button
          key={i}
          onClick={button.onPress}
          className={X.clsx(
            "rounded-full p-1 xl:p-2",
            button.active ? "bg-surface-gray-darker cursor-pointer" : "bg-surface-gray",
          )}
        >
          {button.icon}
        </button>
      ))}
    </div>
  );
}
